'use client'

import { CheckIcon, CircleAlertIcon, InfoIcon, Loader2Icon, TriangleAlertIcon } from 'lucide-react'
import { useTheme } from 'next-themes'
import { Toaster as Sonner, type ToasterProps } from 'sonner'

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      icons={{
        success: <CheckIcon className="size-3.5" strokeWidth={3} />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <CircleAlertIcon className="size-3.5" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
          // The stack's width; each pill hugs its own text inside it.
          '--width': 'min(340px, calc(100vw - 32px))',
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          // A compact pill that hugs its text (centred), with the icon in a round badge.
          toast:
            'cn-toast !inset-x-0 !mx-auto !w-fit !max-w-[min(340px,calc(100vw-32px))] !items-center !gap-2.5 !rounded-full !border-border !bg-card !py-2 !pr-2 !pl-2 !text-card-foreground !shadow-[0_10px_30px_rgba(0,0,0,0.35)] has-[[data-description]]:!rounded-2xl [&:not(:has([data-button]))]:!pr-4 [&_[data-icon]]:!m-0 [&_[data-icon]]:!flex [&_[data-icon]]:!size-[22px] [&_[data-icon]]:!shrink-0 [&_[data-icon]]:!items-center [&_[data-icon]]:!justify-center [&_[data-icon]]:!rounded-full [&_[data-icon]]:bg-muted data-[type=success]:[&_[data-icon]]:!bg-primary data-[type=success]:[&_[data-icon]]:!text-primary-foreground data-[type=error]:[&_[data-icon]]:!bg-destructive/15 data-[type=error]:[&_[data-icon]]:!text-destructive data-[type=warning]:[&_[data-icon]]:!bg-amber-500/15 data-[type=warning]:[&_[data-icon]]:!text-amber-500 data-[type=info]:[&_[data-icon]]:!bg-sky-400/15 data-[type=info]:[&_[data-icon]]:!text-sky-400',
          title: '!text-[13px] !font-semibold !leading-snug',
          description: '!text-xs !text-muted-foreground',
          actionButton:
            '!ml-1 !h-7 !shrink-0 !rounded-full !bg-muted !px-3 !text-xs !font-bold !text-primary-text hover:!bg-muted/70',
          cancelButton: '!h-7 !rounded-full !bg-muted !px-3 !text-xs !text-muted-foreground',
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
